/**
 * LeadDetailsMonitoringTab — the blogger card's «Статистика» tab (tab id `monitoring`).
 *
 * MONITORING2 2026-09-23, spec §3.5 (R9, R14, R16) — reworked IN PLACE. Render order:
 *   1. BonusPanel (unchanged) — the million-videos.
 *   2. The pack header: pack chips (only when the blogger has more than one pack), title,
 *      application chip, «В Мониторинг», and the contract rate tile.
 *   3. Key figures of the selected pack: «Видео» and «Рост факт» (`sumDelta`, the block's
 *      Итого) for the current month (to date) and the previous one, from the SAME `/monthly?sheet=1` query the
 *      «Мониторинг 2» section expands (one React Query key, one request), plus
 *   4. «Управление пачкой» — PackSettingsBlock, an always-visible card for the pack the
 *      chips select: the pack's fields and its parsing switch, then its accounts, one
 *      parsing switch each (the effective bit, PackParsingControls.jsx). No blogger
 *      switch in this tab (R9).
 * The round blocks, the round-status badge, the round close button, round edit/delete and
 * the day matrix have left the tab (R14); the round DB and the old «Мониторинг» section
 * keep theirs.
 *
 * DESIGN SYSTEM: every visual element is a DS component from './ds' on `oc-lead-card__*`
 * classes (components/lead-card.css); the compact figures table is real <table> markup.
 * The tab's root keeps `details-modal__info` as a structural hook.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiClient from '../api/client.js';
import { monitoringPackTarget } from '../lib/monitoringPackTarget.mjs';
import { monthGrowthPct, growthArrow } from '../lib/monthGrowth.mjs';
import { usePacksByLead } from '../api/packs.js';
import { usePackRounds, useAmbPackMonthly } from '../api/rounds.js';
import { Badge, Button, Card, Chip } from './ds';
import BonusPanel from './BonusPanel.jsx';
import PackSettingsBlock from './PackSettingsModal.jsx';
import { GeoState, useGeoReport } from './MonitoringGeoView.jsx';

function todayStr () {
  return new Date().toISOString().slice(0, 10);
}

/** 'YYYY-MM' → the month before it. */
function prevMonthOf (ym) {
  const [y, m] = ym.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** Comma decimal separator; `null` → «—». */
const fmtNum = (v) => (v == null ? '—' : String(v).replace('.', ','));
/** Whole numbers, like the sheet and the «Мониторинг 2» section, so the two compare 1:1. */
const fmtInt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const fmtSigned = (v) => (v < 0 ? `−${fmtInt.format(-v)}` : fmtInt.format(v));

/**
 * The rate tile — CARD-ROUNDS D4. It DISPLAYS the contract's rate and writes nothing:
 * one money entity, the contract (owner ruling 24.08). It reads exactly one field — the
 * endpoint's contract-derived suggestion, `contracts.trial_amount` of the lead's most
 * recent rate-bearing contract, drafts included (routes/packs.js) — and owns no mutation.
 */
function RateTile ({ pack }) {
  const suggestion = pack?.testPriceSuggestion ?? null;
  return (
    <div className="oc-lead-card__ratetile">
      <span className="oc-lead-card__label">Ставка за тест</span>
      {suggestion == null
        ? <span className="oc-lead-card__muted">Нет договора — ставки нет</span>
        : <b>{`$${fmtNum(suggestion)} · из договора`}</b>}
    </div>
  );
}

// ── The pack header ──────────────────────────────────────────────────────────
// The pack switcher, the header line (name · application) and the rate tile. Everything
// that WRITES the pack or its accounts, parsing switches included, lives in PackSettingsBlock.
function PackBlock ({ packs, selectedPack, onSelect }) {
  const navigate = useNavigate();
  const [locating, setLocating] = useState(false);
  const [locateFailed, setLocateFailed] = useState(false);
  if (!selectedPack) return null;
  const packId = selectedPack.id;

  const openInMonitoring = async () => {
    setLocating(true);
    setLocateFailed(false);
    try {
      const { data } = await apiClient.get(`/monitoring2/packs/${packId}/locate`);
      navigate(monitoringPackTarget(data), { state: { monitoringPackId: packId } });
    } catch {
      setLocateFailed(true);
    } finally {
      setLocating(false);
    }
  };

  // PSMO D8 — the priority pack is a CONVENTION, not a column: `min(createdAt)` with an
  // `id` tie-break for determinism. Computed only where it is shown, which is the chip
  // row — and that row exists only when the blogger has more than one pack.
  const priorityPackId = packs.length > 1
    ? packs.reduce((best, p) => {
        if (!best) return p;
        const a = String(p.createdAt ?? '');
        const b = String(best.createdAt ?? '');
        if (a !== b) return a < b ? p : best;
        return String(p.id) < String(best.id) ? p : best;
      }, null)?.id ?? null
    : null;

  return (
    <div className="oc-lead-card__pack">
      {packs.length > 1 && (
        <div className="oc-lead-card__chips">
          {packs.map((p) => {
            const label = (
              <>
                {p.label ?? 'Пачка'}
                {p.id === priorityPackId && (
                  // POST-W1 B2 — the mark is a nested mini-chip, so the chip's own blue
                  // fill stays the single mark of the SELECTED pack.
                  <Badge color="blue" type="contained" className="oc-lead-card__packchip-mark">приоритетная</Badge>
                )}
              </>
            );
            // D17 — the reference's `data-wait` dimming, fed by the pack's own derived
            // sub-status. Two branches, so the mark is one literal class and one title.
            return p.subStatus === 'waiting' ? (
              <Chip
                key={p.id}
                active={p.id === packId}
                onClick={() => onSelect(p.id)}
                className="oc-lead-card__packchip--wait"
                title="ждём старта"
              >{label}</Chip>
            ) : (
              <Chip
                key={p.id}
                active={p.id === packId}
                onClick={() => onSelect(p.id)}
              >{label}</Chip>
            );
          })}
        </div>
      )}

      <div className="oc-lead-card__pack-head">
        <div className="oc-lead-card__sect-label">Пачка ·</div>
        <span className="oc-lead-card__pack-title"><b>{selectedPack.label ?? 'Пачка'}</b></span>
        {selectedPack.application && (
          <span className="oc-lead-card__chip">{selectedPack.application}</span>
        )}
        <span className="oc-lead-card__right">
          {locateFailed && <span className="oc-lead-card__muted">Не удалось найти пачку в Мониторинге</span>}
          <Button hierarchy="tertiary" size="small" disabled={locating} onClick={openInMonitoring}>В Мониторинг</Button>
        </span>
      </div>

      <RateTile pack={selectedPack} />
    </div>
  );
}

// ── Key figures (spec §3.5, R9 + R16) ────────────────────────────────────────

/** One «Рост факт» figure with the section's marks: «—», « · сейчас», « · старт». */
function MarkValue ({ value, state }) {
  if (value == null || state == null || state === 'absent') return <span className="oc-lead-card__muted">—</span>;
  return (
    <b>
      {fmtSigned(value)}
      {state === 'live' && <span className="oc-lead-card__muted"> · сейчас</span>}
      {state === 'opening' && <span className="oc-lead-card__muted"> · старт</span>}
    </b>
  );
}

const fmtCpm = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** «CPM · коэф» of the last completed month — the sheet row's `cpmPerCoef`, as «Мониторинг 2» shows it. */
function PackCpmLine ({ pack, showName }) {
  const q = useAmbPackMonthly(pack.id, { sheet: true });
  const month = prevMonthOf(todayStr().slice(0, 7));
  const cpm = q.data?.pack?.find((r) => r.month === month)?.cpmPerCoef;
  return (
    <div className="oc-lead-card__totals">
      {showName ? `${pack.label || pack.name || 'Пачка'}: ` : ''}CPM за {month}:{' '}
      {cpm?.value == null ? <span>—</span> : <b>{`$${fmtCpm.format(cpm.value)}`}</b>}
    </div>
  );
}

/** «Рост просмотров» last completed month vs the one before, with ▲ / ▼. */
function GrowthLine ({ last, base }) {
  const pct = monthGrowthPct(last, base);
  return (
    <div className="oc-lead-card__totals">
      Рост просмотров к пред. месяцу:{' '}
      {pct == null ? <span>—</span> : (
        <b style={{ color: pct < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
          {growthArrow(pct)} {fmtCpm.format(Math.abs(pct))}%
        </b>
      )}
    </div>
  );
}

function postsCell (row, metricsAvailable) {
  if (!row || !metricsAvailable || row.posts == null) return <span className="oc-lead-card__muted">—</span>;
  return <b>{fmtInt.format(row.posts)}</b>;
}

function KeyFigures ({ pack, packs }) {
  const monthlyQ = useAmbPackMonthly(pack.id, { sheet: true });

  const curMonth = todayStr().slice(0, 7);
  const rows = monthlyQ.data?.pack ?? [];
  const cur = rows.find((r) => r.month === curMonth) ?? null;
  const prev = rows.find((r) => r.month === prevMonthOf(curMonth)) ?? null;
  const prev2 = rows.find((r) => r.month === prevMonthOf(prevMonthOf(curMonth))) ?? null;
  const metricsAvailable = monthlyQ.data?.metricsAvailable !== false;

  return (
    <Card className="oc-lead-card__section">
      <div className="oc-lead-card__sect-label">Ключевые цифры</div>
      {monthlyQ.isLoading ? (
        <div className="oc-lead-card__muted">Загрузка месяцев…</div>
      ) : monthlyQ.isError ? (
        <div className="oc-lead-card__error">Не удалось загрузить месяцы.</div>
      ) : (
        <table className="oc-lead-card__table">
          <thead>
            <tr>
              <th />
              <th className="oc-lead-card__num">Текущий месяц (по сегодня)</th>
              <th className="oc-lead-card__num">Прошлый месяц</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Видео</td>
              <td className="oc-lead-card__num">{postsCell(cur, metricsAvailable)}</td>
              <td className="oc-lead-card__num">{postsCell(prev, metricsAvailable)}</td>
            </tr>
            <tr>
              <td>Рост факт</td>
              <td className="oc-lead-card__num"><MarkValue value={cur?.sumDelta} state={cur?.totalDeltaState} /></td>
              <td className="oc-lead-card__num"><MarkValue value={prev?.sumDelta} state={prev?.totalDeltaState} /></td>
            </tr>
          </tbody>
        </table>
      )}
      <GrowthLine last={prev} base={prev2} />
      {packs.map((p) => <PackCpmLine key={p.id} pack={p} showName={packs.length > 1} />)}
    </Card>
  );
}

/** «Гео (со скринов)» of the selected pack for the current UTC month — the same service and presenter as the Monitoring tab. */
function PackGeo ({ pack }) {
  const query = useGeoReport({ packId: pack.id, month: todayStr().slice(0, 7) });
  const w = query.data?.window;
  return (
    <Card className="oc-lead-card__section" data-geo-card="" aria-busy={query.isFetching}>
      <div className="oc-lead-card__sect-label">Гео (со скринов)</div>
      <GeoState query={query} limit={5} windowMessage="За текущий месяц ещё нет готовых данных" />
      {w && <Link className="oc-btn oc-btn--tertiary oc-btn--small" data-ds-focusable to={`/monitoring2/geo?${new URLSearchParams({ packId: pack.id, startDate: w.startDate, endDate: w.endDate })}`}>Все страны в Мониторинге</Link>}
    </Card>
  );
}

// ── Tab body (hooks live here so the guard below stays hook-free) ────────────

function MonitoringTabBody ({ lead, initialPackId }) {
  const leadId = lead?.id;
  const packsQ = usePacksByLead(leadId);
  const packs = packsQ.data ?? [];

  // D11 — the deep-link seed. The body UNMOUNTS whenever another tab is shown, so this
  // is re-read on every open of the tab.
  const [selectedPackId, setSelectedPackId] = useState(initialPackId ?? null);
  const selectedPack = packs.find((p) => p.id === selectedPackId) ?? packs[0] ?? null;
  const selectPack = (id) => setSelectedPackId(id);

  // Only PackSettingsBlock reads the rounds (its create-round form): `rounds` and
  // `nextNumber` (spec §7 reading 7). Nothing in the tab itself renders a round.
  const roundsQ = usePackRounds(selectedPack?.id);
  const rounds = roundsQ.data?.rounds ?? [];
  const nextNumber = (rounds[0]?.roundNumber ?? 0) + 1;

  if (packsQ.isLoading) return <div className="oc-lead-card__muted">Загрузка пачек…</div>;
  if (packs.length === 0) {
    return (
      <>
        <div className="oc-lead-card__muted">У блогера нет пачек аккаунтов — создай пачку в инвойсах или карточке.</div>
        <div className="oc-lead-card__totals">CPM: <span>—</span></div>
      </>
    );
  }

  return (
    <>
      <PackBlock
        packs={packs}
        selectedPack={selectedPack}
        onSelect={selectPack}
      />
      <KeyFigures key={`kf-${selectedPack.id}`} pack={selectedPack} packs={packs} />
      <PackGeo key={`geo-${selectedPack.id}`} pack={selectedPack} />
      <PackSettingsBlock
        key={`ps-${selectedPack.id}`}
        leadId={leadId}
        pack={selectedPack}
        rounds={rounds}
        nextNumber={nextNumber}
        leadApplication={lead?.metadata?.application ?? null}
      />
    </>
  );
}

export function LeadDetailsMonitoringTab ({ panelTab, lead, initialPackId }) {
  if (panelTab !== 'monitoring') return null;
  return (
    <aside className="details-modal__info oc-lead-card__monitoring">
      <BonusPanel leadId={lead?.id} />
      <MonitoringTabBody lead={lead} initialPackId={initialPackId} />
    </aside>
  );
}

export default LeadDetailsMonitoringTab;
